import { memo } from "react";

/**
 * Full-viewport animated background: soft drifting aurora blobs + sparse
 * floating particles. Mounted once per layout root (Layout / AdminLayout /
 * AdminLogin) alongside BackgroundGrid so every page gets motion behind its
 * content instead of a flat color. Fixed positioning means no scroll-driven
 * work. Respects prefers-reduced-motion via the .gtl-aurora-blob /
 * .gtl-particle rules in index.css.
 */
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  left: (i * 37 + 9) % 100,
  top: (i * 61 + 17) % 100,
  size: i % 3 === 0 ? 4 : 2.5,
  duration: 14 + ((i * 5) % 10),
  delay: (i * 0.9) % 8,
}));

export const AnimatedBackground = memo(() => (
  <div className="fixed inset-0 -z-20 overflow-hidden pointer-events-none" aria-hidden="true">
    <div
      className="gtl-aurora-blob absolute top-[-10%] left-[-10%] w-[52vw] h-[52vw] max-w-[640px] max-h-[640px] bg-cyan-400/45 rounded-full blur-[90px]"
      style={{ animation: "aurora-drift-a 16s ease-in-out infinite" }}
    />
    <div
      className="gtl-aurora-blob absolute top-[35%] right-[-14%] w-[48vw] h-[48vw] max-w-[600px] max-h-[600px] bg-blue-500/38 rounded-full blur-[100px]"
      style={{ animation: "aurora-drift-b 20s ease-in-out infinite" }}
    />
    <div
      className="gtl-aurora-blob absolute bottom-[-15%] left-[20%] w-[42vw] h-[42vw] max-w-[520px] max-h-[520px] bg-[#00AEEF]/32 rounded-full blur-[95px]"
      style={{ animation: "aurora-drift-c 18s ease-in-out infinite reverse" }}
    />
    <div
      className="gtl-aurora-blob absolute top-[65%] left-[45%] w-[34vw] h-[34vw] max-w-[420px] max-h-[420px] bg-cyan-300/28 rounded-full blur-[85px]"
      style={{ animation: "aurora-drift-a 22s ease-in-out infinite reverse" }}
    />
    {PARTICLES.map((p, i) => (
      <div
        key={i}
        className="gtl-particle absolute rounded-full bg-cyan-300/90"
        style={{
          left: `${p.left}%`,
          top: `${p.top}%`,
          width: p.size,
          height: p.size,
          animation: `particle-drift ${p.duration}s linear ${p.delay}s infinite`,
          boxShadow: "0 0 12px 2px rgba(0,229,255,0.85)",
        }}
      />
    ))}
  </div>
));

AnimatedBackground.displayName = "AnimatedBackground";

export default AnimatedBackground;
